/**
 * The first-visit tour: one step per feature. `target` is a CSS selector for the thing to
 * highlight (sections carry `data-tour` anchors); `tab` switches the control panel first.
 * Steps without a target show a centred card.
 */
import type { Tab } from '../app/controller.svelte';
import { ICONS, type IconName } from './icons';

export interface TourStep {
  id: string;
  title: string;
  /** Plain text; `[[X]]` renders as a key cap and `{{name}}` as that icon (see icons.ts). */
  body: string;
  target?: string;
  tab?: Tab;
  /** A screenshot (under public/, made by `pnpm tour:shots`) for things the page can't show. */
  image?: { src: string; alt: string };
}

export const TOUR_STEPS: readonly TourStep[] = [
  {
    id: 'stage',
    title: 'The stage',
    body: 'This short tour shows you around; leave it at any time with [[Esc]]. First, the stage: this is what’s playing. Move the mouse and the controls appear; leave it alone and they fade away. Press [[Alt+Enter]] (or {{fullscreen}}) for fullscreen and [[H]] to hide or show the controls. The keys are split like a game: your left hand plays the show, your right hand (or the mouse) shapes the look; [[?]] shows the map.',
  },
  {
    id: 'tabs',
    title: 'Six sections',
    body: 'Scene is what’s on screen: your layers and their look. Visuals is where you manage visualizations, Presets is saving and favorites, Audio is your music and the beat, Outputs is extra screens and recording, and Settings is everything else.',
    target: '[data-tour="tabs"]',
    tab: 'scene',
  },
  {
    id: 'quick',
    title: 'Surprise me and favorites',
    body: '{{dice}} builds a random new look ([[Shift+R]]). Don’t like it? [[⌘Z]] / [[Ctrl+Z]] takes it back. {{heart}} saves what’s on screen to your favorites ([[F]]; [[Shift+F]] takes it out).',
    target: '[data-tour="quick"]',
    tab: 'scene',
  },
  {
    id: 'layers',
    title: 'Layers',
    body: 'Stack up to six visualizations. Each layer is a section: click its name to open it, and use the menu next to it to choose how it blends with the layers below. For bars, rings or particles drawn on black, “Over” keeps their colours on top.',
    target: '[data-tour="layers"]',
    tab: 'scene',
  },
  {
    id: 'viz',
    title: 'Visualizations',
    body: 'Inside a layer, pick what it shows: full-screen scenes (tunnels, fractals, a black hole…), shapes and particles, or melody visualizations that follow the tune in colour. Filter or search, then click one.',
    target: '[data-tour="viz-browser"]',
    tab: 'scene',
  },
  {
    id: 'params',
    title: 'Everything can move',
    body: 'Drag any slider. {{wave}} makes a value swing back and forth around where you set it. {{modulate}} ties it to the music: the bass, the beat, an LFO and more. With MIDI on, M maps it to a knob.',
    target: '[data-tour="layer-controls"]',
    tab: 'scene',
  },
  {
    id: 'layer-look',
    title: 'A look for each layer',
    body: 'Colours and effects can be shared by every layer or belong to just this one. Put a kaleidoscope on the lasers only, or give the background its own palette: open Colours or Effects and choose “This layer”.',
    target: '[data-tour="layer-look"]',
    tab: 'scene',
  },
  {
    id: 'palette',
    title: 'Overall colours',
    body: 'Every layer without its own palette paints with the overall palette. Pick a built-in one or make your own.',
    target: '[data-testid="acc-overall-colours"]',
    tab: 'scene',
  },
  {
    id: 'effects',
    title: 'Overall effects',
    body: 'Trails, kaleidoscope, bloom, colour grading and lens effects on the finished picture. Below them: motion, the transition into this scene, and your modulations. They all work with {{wave}} and {{modulate}} too.',
    target: '[data-tour="effects"]',
    tab: 'scene',
    image: {
      src: 'tour/effects.jpg',
      alt: 'The same visuals turned into a mirrored pattern by the kaleidoscope effect',
    },
  },
  {
    id: 'auto',
    title: 'Auto show',
    body: 'Let it run the show: it changes one thing at a time on the bar or the phrase (colours, a top layer, the background, the effects), matched to the energy, and the whole look on a drop. [[C]] turns it on; [[V]] changes one thing now.',
    target: '[data-tour="auto"]',
    tab: 'scene',
  },
  {
    id: 'visualizations',
    title: 'Manage visualizations',
    body: 'Upload your own shaders, hide visualizations you don’t want to see, and keep any out of Surprise me and Auto show.',
    target: '[data-tour="viz-manage"]',
    tab: 'visualizations',
  },
  {
    id: 'favorites',
    title: 'Favorites',
    body: 'Everything you {{heart}} lands here, as many as you like. Click one to bring it back; [[Q]] and [[E]] step through them.',
    target: '[data-tour="favorites"]',
    tab: 'presets',
  },
  {
    id: 'banks',
    title: 'Banks for live sets',
    body: 'Ten banks of ten slots, on keys [[1]]–[[0]]. [[Shift]]+number saves the current look to a slot; [[W]] and [[S]] change bank, [[A]] and [[D]] step through presets.',
    target: '[data-tour="banks"]',
    tab: 'presets',
  },
  {
    id: 'source',
    title: 'Your music',
    body: 'Drop audio files or a whole folder anywhere on the page, use a microphone or line-in, or capture a browser tab (Spotify, YouTube…).',
    target: '[data-tour="source"]',
    tab: 'audio',
  },
  {
    id: 'beat',
    title: 'The beat',
    body: 'The tempo is found automatically. If it’s off, tap it with [[T]], mark the first beat of the bar with [[Shift+T]], and nudge with [[Z]] and [[X]]. [[Alt+T]] holds the tempo where it is; the Live pane’s Auto/Hold button does the same.',
    target: '[data-tour="beat"]',
    tab: 'audio',
  },
  {
    id: 'sync',
    title: 'Sync',
    body: 'If the visuals land early or late compared with the sound, run the calibration once for this screen and these speakers.',
    target: '[data-tour="sync"]',
    tab: 'audio',
  },
  {
    id: 'outputs',
    title: 'More screens',
    body: 'Open output windows for projectors and TVs, then mirror the picture on all of them or tile one big picture across them.',
    target: '[data-tour="outputs"]',
    tab: 'outputs',
  },
  {
    id: 'preview',
    title: 'Preview, then go live',
    body: 'Turn on preview mode to prepare the next look in this window while the screens stay on what’s live. [[Space]] fades it in and [[Shift+Space]] cuts; fades can overlap, each layering in at once. Grab the T-bar to take over a fade mid-way, and press Fade to carry on from there. [[Tab]] (or PVW) turns preview mode on and off.',
    target: '[data-tour="preview-section"]',
    tab: 'outputs',
    image: {
      src: 'tour/preview.jpg',
      alt: 'Preview mode: the next look being prepared, with the live picture in the corner and the Cut, Fade and T-bar controls',
    },
  },
  {
    id: 'record',
    title: 'Recording',
    body: 'Save the visuals, with the music, to a video file on this device ([[Alt+R]]).',
    target: '[data-tour="record"]',
    tab: 'outputs',
  },
  {
    id: 'settings',
    title: 'Settings',
    body: 'Grouped by topic. Click a group to open it, or search. Options that depend on a switch appear once you turn that switch on.',
    target: '[data-tour="settings-search"]',
    tab: 'settings',
  },
  {
    id: 'panes',
    title: 'Live pane and strip',
    body: '{{pulse}} shows the Live pane: tempo, a Tap button and the beat ([[K]]). {{strip}} shows a strip of your favorites and banks along the bottom, to load looks with one click ([[J]]). Show or hide them any time.',
    target: '[data-testid="toggle-live-pane"]',
    image: {
      src: 'tour/panes.jpg',
      alt: 'The Live pane with the tempo and Tap button, above a strip of favorites',
    },
  },
  {
    id: 'performance',
    title: 'Performance mode',
    body: 'For a live show: hides the controls and keeps the keys for the visualizer. Press [[Esc]] twice to leave.',
    target: '[data-tour="performance"]',
    image: { src: 'tour/performance.jpg', alt: 'Full-screen visuals with every control hidden' },
  },
  {
    id: 'help',
    title: 'Help is always here',
    body: 'Click ? or press [[?]] for every keyboard shortcut. You can restart this tour from there at any time. Enjoy!',
    target: '[data-tour="help"]',
    image: { src: 'tour/help.jpg', alt: 'The help sheet listing the keyboard shortcuts' },
  },
];

export interface TourBodyPart {
  text: string;
  key: boolean;
  icon?: IconName;
}

/** Split a step body into text, key-cap and icon parts for rendering. */
export function tourBodyParts(body: string): TourBodyPart[] {
  const out: TourBodyPart[] = [];
  const re = /\[\[(.+?)\]\]|\{\{([a-z-]+)\}\}/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(body))) {
    if (m.index > last) out.push({ text: body.slice(last, m.index), key: false });
    if (m[2] !== undefined && m[2] in ICONS)
      out.push({ text: '', key: false, icon: m[2] as IconName });
    else out.push({ text: m[1] ?? m[0], key: m[1] !== undefined });
    last = m.index + m[0].length;
  }
  if (last < body.length) out.push({ text: body.slice(last), key: false });
  return out;
}
